import { SkipNavContent } from "@reach/skip-nav";
import { HeadingPriority } from "atoms/constants/heading-priority";
import Heading from "atoms/typography/heading";
import { AppNameTm } from "constants/app-name-tm";
import { MetaTag, MetaTagTypes } from "models/interfaces/header-data";
import RouteTabs from "molecules/route-tabs/route-tabs";
import BreadcrumbsMenu from "organisms/breadcrumbs/breadcrumbs-menu";
import useMyLiNKTabs from "pages/my-link/use-my-link-tabs";
import React, { useEffect, useMemo } from "react";
import { useHeaderData } from "utilities/contexts/use-header-data-context";
import { useLocalization } from "utilities/hooks/use-localization";
import { NestedRoutesProps } from "utilities/routing/nested-route";
import MyBookmarksList from "./my-bookmarks-list";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import MyBookmarksDashboard from "../my-bookmarks-dashboard/my-bookmarks-dashboard";
import useMyLiNKDefaults from "utilities/hooks/domain/my-link/use-my-link-defaults";
import { MyLiNKTab } from "models/enumerations/my-link-tab";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface MyBookmarksTabProps extends NestedRoutesProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

export const MY_BOOKMARKS_CSS_CLASS_NAME = "c-my-bookmarks-tab";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const MyBookmarksTab = (props) => {
    const { t } = useLocalization();
    const { useMyLiNKRedesign } = useFeatureFlags();
    const { tabs } = useMyLiNKTabs();
    const { defaultMyLiNKTab, setDefaultMyLiNKTab } = useMyLiNKDefaults();

    const META_TAGS = useMemo(
        function buildMetaTags(): Array<MetaTag> {
            return [
                {
                    name: MetaTagTypes.Title,
                    content: AppNameTm,
                },
                {
                    name: MetaTagTypes.Description,
                    content: t("myBookmarksTab-metaTags-description", {
                        appName: AppNameTm,
                    }),
                },
            ];
        },
        [t]
    );

    // Sets this tab to default
    // Note: may want to refactor how tabs work once we can deprecate this feature flag
    // and start adding more tabs
    useEffect(() => {
        if (
            useMyLiNKRedesign &&
            defaultMyLiNKTab !== MyLiNKTab.BookmarksAndCollections
        ) {
            setDefaultMyLiNKTab(MyLiNKTab.BookmarksAndCollections);
        }
    }, [defaultMyLiNKTab, setDefaultMyLiNKTab, useMyLiNKRedesign]);

    useHeaderData({ metaTags: META_TAGS });

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------

    return (
        <>
            {useMyLiNKRedesign ? (
                <MyBookmarksDashboard {...props} />
            ) : (
                <>
                    <BreadcrumbsMenu fullWidth={true} />
                    <SkipNavContent tabIndex={-1}>
                        <div className={MY_BOOKMARKS_CSS_CLASS_NAME}>
                            <div
                                className={`${MY_BOOKMARKS_CSS_CLASS_NAME}__header`}>
                                <Heading
                                    cssClassName={`${MY_BOOKMARKS_CSS_CLASS_NAME}__heading`}
                                    priority={HeadingPriority.Five}>
                                    MyLiNK
                                </Heading>
                                <RouteTabs
                                    redirectIfNotFound={false}
                                    routes={props.routes}
                                    tabs={tabs}
                                />
                            </div>
                            <MyBookmarksList showPagination={true} />
                        </div>
                    </SkipNavContent>
                </>
            )}
        </>
    );

    // #endregion Render
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------
export default MyBookmarksTab;

// #endregion Exports
